import { ChevronRight, type LucideIcon } from "lucide-react";
import { Link } from "react-router";
import { useLocalStorage } from "usehooks-ts";
import { Badge } from "@/components/ui/badge";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { useIsActive } from "./useIsActive";

export type NavAreaItem = {
	title: string;
	url: string;
	icon: LucideIcon;
	/**
	 * @default true
	 */
	defaultExpanded?: boolean;
	items?: {
		title: string;
		url: string;
		count?: number;
		matchExact?: boolean;
	}[];
	matchExact?: boolean;
	count?: number;
};

export function CollapsibleItem({ item }: { item: NavAreaItem }) {
	const isActive = useIsActive(item.url, item.matchExact ?? false);
	const storageKey = `nav-collapsible-${item.title}`;
	const [isOpen, setIsOpen] = useLocalStorage<boolean>(
		storageKey,
		item.defaultExpanded ?? true,
	);

	return (
		<Collapsible key={item.title} open={isOpen} onOpenChange={setIsOpen}>
			<SidebarMenuItem>
				<CollapsibleTrigger
					nativeButton={false}
					render={
						<SidebarMenuButton
							isActive={isActive && !item.items?.length}
							asChild
							tooltip={item.title}
						>
							<Link to={item.url}>
								<item.icon />
								<span>{item.title}</span>
								{(item.count ?? 0) > 0 && (
									<Badge className="h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
										{item.count}
									</Badge>
								)}
							</Link>
						</SidebarMenuButton>
					}
				/>
				{item.items?.length ? (
					<>
						<CollapsibleTrigger
							className="[&[data-panel-open]_svg]:rotate-90"
							render={<SidebarMenuAction />}
						>
							<ChevronRight />
							<span className="sr-only">Toggle</span>
						</CollapsibleTrigger>
						<CollapsibleContent>
							<SidebarMenuSub>
								{item.items?.map((subItem) => (
									<NestedItem key={subItem.title} item={subItem} />
								))}
							</SidebarMenuSub>
						</CollapsibleContent>
					</>
				) : null}
			</SidebarMenuItem>
		</Collapsible>
	);
}

function NestedItem({
	item,
}: {
	item: {
		title: string;
		url: string;
		count?: number;
		matchExact?: boolean;
	};
}) {
	const isActive = useIsActive(item.url, item.matchExact ?? false);
	return (
		<SidebarMenuSubItem>
			<SidebarMenuSubButton isActive={isActive} asChild>
				<Link to={item.url}>
					<span>{item.title}</span>
					{item.count && (
						<Badge className="h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
							{item.count}
						</Badge>
					)}
				</Link>
			</SidebarMenuSubButton>
		</SidebarMenuSubItem>
	);
}
